<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
            
        <div class="row">

            <div class="col-12 mt-5">

                <div class="h-auto bg-white border shadow-sm rounded-lg overflow-hidden" style="min-height: 50px;">
                    <transition name="fade-fast" mode="out-in">
                        <ol 
                            id="rfx-steps"
                            v-if="!loading && !transitionLoading"
                            class="w-100 h-100 d-flex flex-wrap list-unstyled mb-0"
                        >  <!-- DO NOT DISABLE THESE IF ARCHIVED -- changing state is the one thing you should be able to do when archived -->
                            <li 
                                v-for="(state, index) in stateSelectorStates"
                                :key="state.name"
                                class="rfx-steps-inner position-relative d-flex justify-content-center align-items-center"
                                :class="{
                                    selected: state.isSelected,
                                    unavailable: !state.isAvailable,
                                }"
                                style="flex-basis: 0%; flex-grow: 1; flex-shrink: 1;"
                            >
                                <button 
                                    class="rfx-steps-button h-100 w-100 d-flex align-items-center justify-content-center"
                                    :disabled="!state.isAvailable || !rtuIsAdmin"
                                    @click="callTrigger(state.triggerName)"
                                >
                                    <div>
                                        <b-icon :icon="state.icon" :variant="state.color_variant" class="rounded-circle" :class="`bg-${state.color_variant}-100`" style="padding: 10px;" font-scale="3"></b-icon>
                                    </div>
                                    <div class="ml-3 text-left">
                                        <div class="font-weight-bold text-title">{{ state.title }}</div>
                                        <div class="small text-muted text-description">{{ state.text }}</div>
                                    </div>
                                </button>
                                <div 
                                    v-if="(index + 1) < stateSelectorStates.length"
                                    class="d-none d-md-block position-absolute h-100" style="top: 0; right: 0; width: 20px;" aria-hidden="true"
                                >
                                    <svg class="h-100 w-100 text-gray-300" viewBox="0 0 22 80" fill="none" preserveAspectRatio="none">
                                        <path d="M0 -2L20 40L0 82" vector-effect="non-scaling-stroke" stroke="currentcolor" stroke-linejoin="round" />
                                    </svg>
                                </div>
                                <div
                                    class="selected-bar" 
                                    :style="{
                                        width: ((index + 1) < stateSelectorStates.length) ? 'calc(100% - 2px)' : 'calc(100% + 18px)',
                                        backgroundColor: state.isSelected ? `var(--${state.color_variant})` : '',
                                    }"
                                ></div>
                            </li>
                        </ol>
                    </transition>
                </div>

            </div>

            <div class="col-12 mt-5">
                <div class="mb-2">
                    <h5 class="d-inline-block text-dark mb-0">Settings</h5>
                    <b-link v-if="rtuIsAdmin" class="special-text ml-3" :href="rfxData.internal_setup_url">Manage</b-link>
                </div>
                <div class="border rounded shadow-sm bg-white p-3">
                    <div class="row">
                        <div class="col-4 mb-4">
                            <hot-edit-select
                                label="Event Type"
                                required
                                ref="type"
                                v-model="rfxData.rfx_type"
                                label-vertical
                                :edit-mode="false"
                                no-edit-class="font-size-100 font-weight-500"
                                :options="rfxData.rfx_type_options"
                                blank-option="-- N/A --"
                                :loading="loading"
                                class="min-width-200"
                            ></hot-edit-select>
                        </div>
                        <div class="col-4 mb-4">
                            <hot-edit-select
                                label="Category"
                                ref="category"
                                v-model="rfxData.sourcing_category_id"
                                label-vertical
                                :edit-mode="false"
                                no-edit-class="font-size-100 font-weight-500"
                                :options="rfxData.sourcing_category_options"
                                blank-option="-- none --"
                                :loading="loading"
                                class="min-width-200"
                            ></hot-edit-select>
                        </div>
                        <div class="col-4 mb-4">
                            <p class="hot-edit-label mb-1">Sourcing Tags</p>
                            <div>
                                <span 
                                    v-for="tag in rfxData.sourcing_tags"
                                    :key="tag.id"
                                    
                                    class="chip chip-primary font-size-80 mr-2"
                                >{{ tag.name }}
                                </span>
                            </div>
                        </div>
                        <div class="col-4">
                            <hot-edit-date-time
                                label="Response Open Date"
                                ref="startDate"
                                popover-content="This is the date when suppliers can start viewing the RFx and submitting responses.  Optional."
                                v-model="rfxData.open_date_to_show"
                                :edit-mode="false"
                                no-edit-class="font-size-100 font-weight-500"
                                label-vertical
                                empty-text="Not Currently Open"
                                :loading="loading"
                                class="min-width-200"
                            ></hot-edit-date-time>
                        </div>
                        <div class="col-4">
                            <hot-edit-date-time
                                label="Response Close Date"
                                ref="closeDate"
                                popover-content="This is the deadline for accepting responses.  If set, the event status will automatically be changed to 'Review' on this date.  Optional."
                                v-model="rfxData.close_date_to_show"
                                :edit-mode="false"
                                no-edit-class="font-size-100 font-weight-500"
                                label-vertical
                                :loading="loading"
                                class="min-width-200"
                            ></hot-edit-date-time>
                        </div> 
                        <div class="col-4 mb-4">
                            <hot-edit-input
                                label="Event Owner"
                                required
                                ref="owner"
                                v-model="rfxData.sourcing_owner_name"
                                label-vertical
                                :edit-mode="false"
                                no-edit-class="font-size-100 font-weight-500"
                                :loading="loading"
                                class="min-width-200"
                            ></hot-edit-input>
                        </div>
                    </div>
                </div>
            </div>

            <div 
                class="col-12 mt-5"
            >    
                <div class="mb-2">
                    <h5 class="d-inline-block text-dark mb-0">Invitations & Responses</h5>
                    <b-link :href="rfxData.internal_manage_url" class="special-text ml-3">View All</b-link>
                </div>
                <div class="border rounded shadow-sm bg-white px-4 py-3">
                    <div class="d-flex flex-wrap gap-4 align-items-stretch">
                        <div
                            v-for="group in vendorsByState" 
                            :key="group.title"
                            class="flex-fill border rounded h-100 d-flex align-items-stretch overflow-hidden"
                            :class="group.classes"
                        >  <!-- Invitations -->
                            <div 
                                class="px-3 py-2 d-flex align-items-center flex-fill"
                                :style="`border-left: 4px solid var(--${group.variant});`"
                            >
                                <div>
                                    <h6 :class="group.titleClasses">{{group.title}}</h6>
                                    <b-link v-if="group.url" :href="group.url" class="small special-text">{{group.urlText}}</b-link>
                                </div>
                                <div 
                                    v-b-tooltip.hover
                                    :title="group.names.join(', ')" 
                                    class="text-muted font-size-180 ml-4 min-width-40"
                                >
                                    <pholder-text :text="group.count" :force-show="!loading"></pholder-text>
                                </div>
                            </div>
                            <div 
                                v-for="child in group.children"
                                :key="child.title"
                                class="px-3 py-2 d-flex align-items-center border-left flex-fill">
                                <div 
                                    v-b-tooltip.hover
                                    :title="child.names.join(', ')" 
                                    class="h-100 text-center text-muted flex-fill d-flex flex-column justify-content-between align-items-center"
                                >
                                    <div class="text-uppercase font-size-70 font-weight-500 text-wrap" v-html="child.title"></div>
                                    <div class="font-size-110 text-dark min-width-40">
                                        <pholder-text :text="child.count" :force-show="!loading"></pholder-text>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div 
            v-if="rtuIsAdmin"
            class="row row-cols-2 pb-4"
        >

            <div class="col mt-5">
                <div class="d-flex mb-2">
                    <div>
                        <h5 class="d-block text-dark mb-1">Quick Actions</h5>
                        <p class="text-muted small mb-0">
                            Shortcuts to help manage the sourcing event.
                        </p>
                    </div>
                </div>
                
                <div
                    v-if="loading || transitionLoading"
                    key="loadingActions"
                    class="min-height-150 height-150"
                >
                    <busy-spinner></busy-spinner>
                </div>
                <b-card
                    no-body
                    v-else
                    key="actionsLoaded"
                    class="shadow-sm rounded animate__animated animate__fast animate__fadeIn"
                >
                    <b-list-group flush id="rfx-transitions">
                        <b-list-group-item
                            v-for="trans in quickActionTransitions"
                            :key="trans.trigger"
                            href="#"
                            @click="triggerTransition(trans)"
                            class="d-flex justify-content-between align-items-center"
                        >
                            <div class="font-weight-bold w-35 d-flex align-items-center">
                                <b-icon :icon="trans.icon" :variant="trans.color_variant" class="rounded-circle" :class="`bg-${trans.color_variant}-100`" style="padding: 6px;" font-scale="2"></b-icon>
                                <span class="ml-2">{{ trans.label }}</span>
                            </div>
                            <div class="small w-50">
                                {{ trans.description }}
                            </div>
                            <div class="text-right w-15">
                                <span class="special-text text-primary">Go &rarr;</span>
                            </div>
                        </b-list-group-item>
                        <b-list-group-item
                            href="delete/"
                            class="d-flex justify-content-between align-items-center"
                        >
                            <div class="font-weight-bold w-35 d-flex align-items-center">
                                <b-icon icon="trash" variant="danger" class="rounded-circle bg-gray-100" style="padding: 6px;" font-scale="2"></b-icon>
                                <span class="ml-2">Delete Event</span>
                            </div>
                            <div class="small w-50">
                                Permanently delete this sourcing event and all related vendor responses and response forms.
                            </div>
                            <div class="text-right w-15">
                                <span class="special-text text-primary">Delete &rarr;</span>
                            </div>
                        </b-list-group-item>
                        <!-- PUT CLONE BUTTON HERE - IMPLEMENT IT! -->
                        <!-- <b-list-group-item
                            href="#"
                            disabled
                            class="d-flex justify-content-between align-items-center"
                        >
                            <div class="font-weight-bold w-30">
                                Clone
                            </div>
                            <div class="small w-55">
                                Create a new duplicate of this event.
                            </div>
                            <div class="text-right w-15">
                                <span class="special-text text-primary">Clone &rarr;</span>
                            </div>
                        </b-list-group-item> -->
                        <b-list-group-item
                            v-for="wf in rfxWorkflowSpecs"
                            :key="wf.id"
                            button
                            @click="triggerWorkflowStarter(wf.id)"
                            class="d-flex justify-content-between align-items-center"
                        >
                            <div class="font-weight-bold w-85 d-flex align-items-center">
                                <b-icon icon="play" variant="primary" class="rounded-circle bg-primary-100" style="padding: 6px;" font-scale="2"></b-icon>
                                <span class="ml-2">Start {{wf.name}}</span>
                            </div>
                            <!-- <div class="small w-50">
                                Put workflow spec description here if you ever have one
                            </div> -->
                            <div class="text-right w-15">
                                <span class="special-text text-primary">Go &rarr;</span>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </b-card>
                <rfx-sourcing-workflow-list
                    v-if="showRfxSourcingWorkflowList"
                    :rfx-id="rfxPk"
                    class="mt-3"
                    wrapper-class="card shadow-sm rounded list-group list-group-flush animate__animated animate__fast animate__fadeIn"
                >  <!-- NOTE - this also collapses (hides) itself completely if there are no workflows to show -->
                    <template #header>
                        <div>
                        <h5 class="d-block text-dark mb-1">Related Workflows</h5>
                    </div>
                    </template>
                    <template #list="{ workflows }">
                        <b-list-group-item 
                            v-for="wf in workflows" 
                            :key="wf.id"
                            :href="wf.urls.generic_redirect_url"
                            target="_blank"
                            class="d-flex justify-content-between align-items-center"
                        >
                            <div class="font-weight-bold w-85 d-flex align-items-center">
                                <b-icon icon="person-check-fill" variant="primary" class="rounded-circle bg-primary-100" style="padding: 6px;" font-scale="2"></b-icon>
                                <div class="ml-2">
                                    <div class="">{{wf.title}}</div>
                                    <div class="d-flex gap-4 align-items-baseline small text-muted">
                                        <div>started {{ formatDate(wf.created) }}</div>
                                        <div v-if="wf.current_step_title" class="font-weight-bold">{{ wf.current_step_title }}</div>
                                        <div v-if="wf.vendor && wf.vendor.name">{{ wf.vendor.name }}</div>
                                    </div>
                                </div>
                            </div>
                            <div class="text-right w-15">
                                <span class="special-text text-primary">Go &rarr;</span>
                            </div>
                        </b-list-group-item>
                    </template>
                </rfx-sourcing-workflow-list>
            </div>

            <div class="col mt-5">
                <div class="d-flex justify-content-between align-items-end mb-2">
                    <div>
                        <h5 class="d-block text-dark mb-1">Bulk Messages</h5>
                        <p class="text-muted small mb-0">
                            Send separate emails to many suppliers at once regarding this RFX.
                        </p>
                    </div>
                    
                    <b-button
                        variant="gray-success"
                        size="sm"
                        :disabled="isArchived"
                        @click="createNewMessage"
                    >  <!-- we will always allow sending new bulk messages, no matter what status, so vendors could send something to everyone who was invited to some old event -->
                        <b-icon icon="plus-circle" class="mr-1"></b-icon>
                        New Message
                    </b-button>
                    <!-- <b-link class="special-text ml-3">View All</b-link> -->
                </div>
                <div class="border rounded shadow-sm bg-white p-3">

                    
                    <rfx-bulk-message-list
                        :rfx-pk="rfxPk"
                        ref="bulkMessageList"
                    ></rfx-bulk-message-list>

                </div>
            </div>

            <rfx-bulk-reminder-modal
                ref="bulkReminderModal"
                :rfx-id="rfxPk"
            ></rfx-bulk-reminder-modal>

            <state-machine-transitions
                ref="transitionComponent"
                @state-change="handleStateChange"
                @cancel="onStateChangeCancel"
                @loading="handleStateMachineLoading"
            ></state-machine-transitions>

            <workflow-starter
                ref="workflowStarter"
                spec-groups="_rfx_workflows"
                :extraCreateData="{rfx_id: rfxPk}"
                eager
                @options-loaded="handleWorkflowOptionsLoaded"
            ></workflow-starter>

        </div>
    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import RfxBulkMessageList from './RfxBulkMessageList';
import RfxBulkReminderModal from './RfxBulkReminderModal';
import isEmpty from 'lodash/isEmpty';
import { 
    HotEditInput,
    HotEditSelect,
    HotEditDateTime,
    } from '../js/custom-form-components';
import { Rfx } from '../js/models/rfx';
import StateMachineTransitions from './StateMachineTransitions';
import { currentUserInfoMixin, errorHandling } from '../js/custom-vue-mixins';
import WorkflowStarter from './demo-workflow/components/WorkflowStarter.vue';
import RfxSourcingWorkflowList from './rfx_internal/RfxSourcingWorkflowList.vue';
import { FormatDateHelper } from '../js/filters-helpers';

export default {
    name: 'RfxEventDashboard',
    components: {
        RfxBulkMessageList,
        HotEditInput,
        HotEditSelect,
        HotEditDateTime,
        RfxBulkReminderModal,
        StateMachineTransitions,
        WorkflowStarter,
        RfxSourcingWorkflowList,
    },
    mixins: [
        currentUserInfoMixin,
        errorHandling,
    ],
    props: {
        rfxPk: {
            type: [String, Number],
            required: true,
        },
        rtuRole: {  // role of the RfxTenantUser - 'admin', 'decision', 'requester'
            type: String,
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            transitionLoading: false,  // set this in @loading handler for StateMachineTransitions component
            rfxData: {},
            responseCounts: {},  // object of responses, nested by invites/status
            tags: ['first tag', 'second tag'],  // replace this and link to real tags for the RFX
            selectorTriggersByDestination: {  // READ-ONLY list of transitions that you'll handle through the selector bar
                // look at these to know which transitions to call from selector
                // and which to call from 'Quick Actions'
                draft: ['go_draft_from_any'],
                open: ['go_open_from_draft', 'go_reopen'],
                closed: ['go_close_submissions', 'go_closed_from_archived'],  // we are NOT enabling 'go_close_and_lock'... only can have one per state in selectors.  use action button for that
                archived: ['go_archived_from_draft', 'go_archived_from_open', 'go_archived_from_closed'],
            },
            loadingWorkflowSpecs: false,
            rfxWorkflowSpecs: [],  // get from API
        }
    },
    computed: {
        hasData: function() {
            return !isEmpty(this.rfxData);
        },
        rtuIsAdmin: function() {  // is the current TU an admin for this event?
            // Dec 5 2021 - also allowing tenant admins / super admins
            const isTenantAdmin = this.$currentUserInfo.is_tenant_admin;
            return (this.rtuRole === 'admin') || isTenantAdmin;  
        },
        isArchived() {
            // pull this to top level to make it easy to disable stuff when its archived
            return this.rfxData?.sm_state === 'archived';
        },
        availableStates() {
            // returns an array of all RFX state names that are available to go to, based on possible_transitions
            if (!this.rfxData || !this.rfxData.possible_transitions) return [];
            return this.rfxData.possible_transitions.map(trans => trans.dest);  // might be duplicates in this due to multiple triggers / reflexive transitions, etc.
        },
        availableTriggers() {
            // returns an array of all transition trigger names that are available to use, based on possible_transitions
            if (!this.rfxData || !this.rfxData.possible_transitions) return [];
            return this.rfxData.possible_transitions.map(trans => trans.trigger); 
        },
        possibleTransitions() {
            // pull out possible_transitions array to top level, used many places
            return this.rfxData?.possible_transitions || []; 
        },
        allSelectorBarTriggerNames() {
            // return a flat array of all trigger names that we'll handle through selector bar
            return Object.values(this.selectorTriggersByDestination).flat();
        },
        quickActionTransitions() {
            // returns an array of transitions from possible_transitions that are NOT already handled from the selector bar
            const selectorBarTriggerNames = Object.values(this.selectorTriggersByDestination).flat();
            return this.possibleTransitions.filter(trans => !selectorBarTriggerNames.includes(trans.trigger));
        },
        stateSelectorStates() {
            // return an array of states to display in the state selector
            // we'll always return all the states, but will add disabled, unavailable, etc.
            // we'll take the array from API, pull out what we need?, add some other keys for UI things, return that
            // also will add the triggerName to call, if any... there will only be one or zero
            const states = this.rfxData?.state_options;
            if (!states) return [];  // return empty array until we have something
            // internal function to get the name of available trigger to GO TO this state -- the state and trigger must be available, and the trigger must be one we allow calling from the selector bar (ie: not reflexive transitions, etc.)
            // it will return empty string if no available trigger to go to this state 
            const getAvailableTrigger = (stateName) => {
                const enabledSelectorTriggers = this.selectorTriggersByDestination[stateName];  // all possible triggers available in the selector bar to go to this state
                if (!this.availableStates.includes(stateName)) return '';  // return early if the state isn't available 
                return enabledSelectorTriggers.find(trigName => this.availableTriggers.includes(trigName)) || '';  // will return the first trigger it finds... should only be one... we only want to allow one transition per source/destination pair from the selector bar
            }  // do not have to inclue current state -- we style that separately, and it should not actually be selectable
            return states.map(st => {  // use camelCase for the keys you add, since all backend ones will be snake_case
                const availableTrigger = getAvailableTrigger(st.name);  // will be '' if no trigger available
                return { 
                    ...st,
                    isAvailable: !!availableTrigger,
                    isSelected: (this.rfxData.sm_state === st.name),
                    triggerName: availableTrigger, 
                };
            })
        },
        vendorsByState() {
            // take data from vendor_names_by_state and output the ones you want to show, 
            // structured nicely for use in templates above, split into groups
            // used to have sub-groups, 'all' and 'invites' but got rid of that Nov 22 2021
            const vByState = this.rfxData.vendor_names_by_state ?? {};  // defaults so things show before loaded
            
            const countAndNamesObj = (arr) => {  // just a helper for below
                if (!arr || !Array.isArray(arr) || arr.length < 1) return { count: 0, names: [] };
                return { count: arr.length, names: arr };
            }
            const groupOneChildren = [
                { title: 'Open Invite', url: '', ...countAndNamesObj(vByState.invited) },
                { title: 'Started', url: '', ...countAndNamesObj(vByState.started) },
                { title: 'Submitted', url: '', ...countAndNamesObj(vByState.submitted) },
                { title: 'Awarded', url: '', ...countAndNamesObj(vByState.awarded) },
            ]
            const groupOneAll = groupOneChildren.map(child => child.names).flat();
            const groupOne = {
                title: 'Active',
                titleClasses: ['text-success'],
                url: this.rfxData.internal_manage_url,  // if the TU should not be able to see the manage page, then set this to empty string - but for now, we assume all TUs who can see the RFX can see the manage page
                urlText: 'View',
                variant: 'success',
                classes: ['bg-gray-50'],
                count: groupOneAll.length,
                names: groupOneAll,
                children: groupOneChildren,
            };
            const groupTwoChildren = [
                { title: 'Declined', url: '', ...countAndNamesObj(vByState.declined) },
                { title: 'Cancelled', url: '', ...countAndNamesObj(vByState.cancelled) },
                { title: 'Expired', url: '', ...countAndNamesObj(vByState.unresponded) },
                { title: 'Rejected', url: '', ...countAndNamesObj(vByState.rejected) },
            ]
            const groupTwoAll = groupTwoChildren.map(child => child.names).flat();
            const groupTwo = {
                title: 'Inactive',
                titleClasses: ['text-danger'],
                url: this.rfxData.internal_manage_url,  // if the TU should not be able to see the manage page, then set this to empty string - but for now, we assume all TUs who can see the RFX can see the manage page
                urlText: 'View',
                variant: 'danger',
                classes: ['bg-gray-50'],
                count: groupTwoAll.length,
                names: groupTwoAll,
                children: groupTwoChildren,
            };
            return [ groupOne, groupTwo ];
        },
        showRfxSourcingWorkflowList() {
            // do we even show the 'related workflow' component?
            // it hides itself if there aren't any, but we use this to basically refresh it when needed
            // for now we'll just hide it if main things are loading
            return !this.loading && !this.transitionLoading;
        },
    },
    created() {
        this.loading = true;
    },
    mounted: function() {
        this.getDashboardData();
        //this.getRfxWorkflowSpecs();
    },
    methods: {
        // updated Feb 26 2022 - added apiErrorHandler via mixin
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        refreshPage() {
            // couple things need to be done at once... have to update the header too
            this.getDashboardData();  // need to refresh this because other stuff on the page may be conditional on status
            this.$root.$refs.rfxInternalHeader?.refreshData();
        },
        async getDashboardData() {
            this.loading = true;
            // UPDATED FEB 26 2022 - using async/await and apiErrorHandler
            try {
                const { data } = await suppliosApi.rfx.getDashboardData(this.rfxPk);
                this.rfxData = new Rfx(data);
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loading = false;
        },
        handleWorkflowOptionsLoaded(wfOptions) {
            // called when WorkflowStarter has fetched available options 
            // we get a list of possible options, we'll display them and have separate method for calling them on this component
            console.log('From handleWorkflowOptionsLoaded', wfOptions);
            if (!!wfOptions && Array.isArray(wfOptions)) {
                this.rfxWorkflowSpecs = [...wfOptions];
            }
        },
        triggerWorkflowStarter(wfId) {
            // call this to 'select' a workflow we want to start
            // we call a method on the component to get the schema and show the workflow starter modal
            console.log(`We want to start WF Spec ${wfId}`);
            const wfStarterComp = this.$refs['workflowStarter'];
            if (wfStarterComp) {
                wfStarterComp.show(wfId);  // can call show() with or without wf spec ID - calling with ID 'pre-selects' which one to start
            }
        },
        async getRfxWorkflowSpecs() {
            // get all _sourcing_module WorkflowSpec instances that have rfx_trigger=true in WorkflowSpec settings/metadata_schema
            this.loadingWorkflowSpecs = true;
            try {
                const axiosConfig = {};
                axiosConfig['params'] = {
                    spec_groups: '_rfx_workflows',
                };
                const { data } = await suppliosApi.workflowSpec.list_for_create(axiosConfig);
                this.rfxWorkflowSpecs = data;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            this.loadingWorkflowSpecs = false;
        },
        createNewMessage: function() {
            this.$refs.bulkMessageList.startCreateMessage();
        },
        startBulkReminder: function() {
            this.$refs.bulkReminderModal.openModal();
        },
        handleStateMachineLoading(loadingState) {
            // handler for @loading on transition component - payload will be boolean of its loading state
            this.transitionLoading = loadingState;
        },
        callTrigger(triggerName) {
            // this gets called from our 'state selector' bar -- don't have transition objects there, at least currently
            // will be name of a trigger that SHOULD be available, but not all are available from selector bar  (there may be multiple transitions targeting the same destination state, we only allow one)
            // we will find the full transition object for this trigger, then use the transition component to call it
            const transitionObj = this.possibleTransitions.find(trans => trans.trigger === triggerName);
            if (!transitionObj) return;
            this.triggerTransition(transitionObj);
        },
        triggerTransition(transObj) {
            // call this from action buttons, or from selector bar (via trigger name -> transition object method above), etc. in this component, getting trigger from possible_transitions
            // use transition component to call the transition, showing modal to collect_data if needed
            // using NEW generic version of StateMachineTransition component - so we need to pass in full transition object, string name of suppliosApi endpoint, and object ID
            console.log(`From parent component, we will try calling trigger >${transObj.trigger}< using transition component`);
            const transComp = this.$refs['transitionComponent'];
            if (!transComp) return;
            transComp.trigger(transObj, 'rfx.call_transition', this.rfxPk);
        },
        handleStateChange(apiResp) {
            // apiResp will be from transitions component -- likely { success: true, message: 'successful', object: rfxData (sparse) }
            console.log('STATE CHANGED - RESPONSE BELOW');
            console.log(apiResp);
            this.refreshPage();  // need to refresh this because other stuff on the page may be conditional on status
        },
        onStateChangeCancel() {
            // called anytime the state change modal / process is cancelled
            console.log('state change cancelled');
        },
        formatDate(value) {
            return FormatDateHelper(value);
        },
    }
}
</script>

<style lang="scss">

#rfx-dashboard-action-list-group {
    .list-group-item {
        &.disabled {
            color: rgba(0,0,0,0.5);
            background-color: var(--gray-100);
        }

    }
}

#rfx-steps {
    background-color: #fff;

    .rfx-steps-inner {
        // this is the li for each step, button and bar and arrow are direct children
        // this is what gets .selected
        transition: 0.2s all;

        button.rfx-steps-button {
            background-color: #fff;  // to override normal button styles
            border: none;  // override button styles
            padding: 0.75rem 1rem;
            transition: 0.2s all;
            //border-bottom: 4px solid transparent;
            .text-title {
                transition: 0.2s all;
                color: $gray-600;
            }
            .b-icon {transition: 0.2s all;}
        }
        .selected-bar {
            background-color: transparent;
            transition: 0.2s all;
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            position: absolute;
            bottom: 0; 
            left: -18px; 
            height: 4px;
        }
        &:hover {
            //border-bottom-color: var(--gray-200);
            button.rfx-steps-button {
                .text-title {
                    color: $gray-900;
                }
            }
            .selected-bar {
                //display: block;
                background-color: $gray-200;
            }
        }
        &.selected {
            //border-bottom-color: var(--primary);
            button.rfx-steps-button {
                .text-title {
                    color: $gray-900;
                }
            }
            .selected-bar {
                background-color: var(--primary);
            }
        }
        &:not(.selected):not(:hover) {
            button.rfx-steps-button {
                .b-icon {
                    background-color: $gray-100;  // remove colored background and text on non-active, non-hover
                    color: $gray-400 !important;  // need !important to override important on text-success/text-danger etc
                }
                /* .text-title {
                    color: $gray-500;
                } */
            }
        }
        &.unavailable:not(.selected) {
            button.rfx-steps-button {
                cursor: not-allowed;
                .text-title {
                    color: $gray-400;
                }
                .b-icon {
                    background-color: $gray-100;  // remove colored background and text on non-active, non-hover
                    color: $gray-400 !important;  // need !important to override important on text-success/text-danger etc
                }
            }
            .selected-bar {
                background-color: transparent;
            }
        }
        


    }

    
}


#rfx-state-picker {
    // the list-group-item elements here are button elements
    .list-group-item {
        padding: 0;  // put it on inner

        .list-group-item-inner {
            padding: 0.75rem 1rem;
            border-bottom: 4px solid transparent;
        }
        
        &.selected {
            background-color: #fff;
            .list-group-item-inner {
                border-bottom-color: var(--primary);
            //background-color: #fff;
            }
        }
        &:not(.selected):not(:hover) {
            .b-icon {
                background-color: $gray-100;  // remove colored background and text on non-active, non-hover
                color: $gray-400 !important;  // need !important to override important on text-success/text-danger etc
            }
        }
    }
}

</style>